import { Component, type ReactNode, type ErrorInfo } from 'react';
import { Card } from './Card';
import { Button } from './Button';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  context?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error(`[ErrorBoundary${this.props.context ? ` — ${this.props.context}` : ''}]`, error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <Card className="text-center py-8">
          <div className="flex flex-col items-center gap-3">
            <svg className="h-8 w-8 text-error" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
            <div>
              <p className="text-sm font-medium text-text-primary">
                Something went wrong{this.props.context ? ` in ${this.props.context}` : ''}
              </p>
              <p className="mt-1 text-xs text-text-muted max-w-sm mx-auto">
                {this.state.error?.message || 'An unexpected error occurred'}
              </p>
            </div>
            <Button size="sm" variant="secondary" onClick={this.handleReset}>
              Try Again
            </Button>
          </div>
        </Card>
      );
    }

    return this.props.children;
  }
}
